/**
 * Vendor Coupons & Deals — Practitioner Hub tab
 * Manage manual codes (coupons) and automatic deals (BOGO, bundle, etc.).
 * Customers apply these on the vendor's storefront page only.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Tag, Sparkles, AlertCircle, Edit3, X, Check } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

const styles = `
  .cd-wrap { padding: 0; color: #f5f0e8; font-family: 'Nunito Sans', sans-serif; }
  .cd-section { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 24px; margin-bottom: 24px; }
  .cd-section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
  .cd-section-title { font-family: 'Cormorant Garamond', serif; font-size: 22px; margin: 0; display:flex; align-items:center; gap:10px; }
  .cd-section-sub { font-size: 12px; color: rgba(245,240,232,0.55); margin: 4px 0 0; }
  .cd-list { display: flex; flex-direction: column; gap: 10px; }
  .cd-row { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; padding:14px 16px; background: rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.08); border-radius: 10px; }
  .cd-row-meta { font-size: 12px; color: rgba(245,240,232,0.55); margin-top: 4px; display:flex; gap:12px; flex-wrap:wrap; }
  .cd-row-title { font-weight: 600; font-size: 15px; display:flex; align-items:center; gap:8px; }
  .cd-code-chip { background: rgba(201,168,76,0.18); color: #e6c879; padding: 3px 10px; border-radius: 6px; font-family: monospace; font-size: 12px; letter-spacing: 0.5px; }
  .cd-empty { padding: 24px; text-align:center; color: rgba(245,240,232,0.5); font-style: italic; }
  .cd-actions { display:flex; gap:6px; }
  .cd-icon-btn { background:none; border:1px solid rgba(255,255,255,0.1); color: rgba(245,240,232,0.7); padding:6px 10px; border-radius: 6px; cursor:pointer; font-size:12px; display:flex; align-items:center; gap:6px; }
  .cd-icon-btn:hover { color:#f5f0e8; border-color: rgba(255,255,255,0.25); }
  .cd-icon-btn.danger:hover { color:#ff8b8b; border-color:#7a2a2a; }
  .cd-modal-bg { position:fixed; inset:0; background:rgba(0,0,0,0.7); display:flex; align-items:center; justify-content:center; z-index: 100; padding:24px; }
  .cd-modal { background:#1a1410; border:1px solid rgba(255,255,255,0.12); border-radius:14px; padding:28px; width: 100%; max-width: 540px; max-height:90vh; overflow-y:auto; }
  .cd-modal-head { display:flex; justify-content:space-between; align-items:center; margin-bottom: 20px; }
  .cd-modal-title { font-family:'Cormorant Garamond', serif; font-size:22px; margin:0; }
  .cd-field { margin-bottom: 16px; }
  .cd-field label { display:block; font-size:12px; color:rgba(245,240,232,0.7); margin-bottom:6px; text-transform:uppercase; letter-spacing:0.5px; }
  .cd-grid-2 { display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .cd-modal-foot { display:flex; justify-content:flex-end; gap:8px; margin-top: 20px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.08); }
  .cd-error { background: rgba(180,40,40,0.15); border:1px solid rgba(180,40,40,0.4); padding: 10px 12px; border-radius: 8px; color:#ffb0b0; font-size:13px; display:flex; gap:8px; margin-bottom:12px; }
  .cd-badge { font-size: 10px; padding: 2px 8px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
  .cd-badge.active { background: rgba(101,179,127,0.2); color: #9fdfb5; }
  .cd-badge.inactive { background: rgba(120,120,120,0.2); color: rgba(245,240,232,0.5); }
  .cd-badge.expired { background: rgba(200,120,40,0.18); color: #f1b975; }
`;

const DISCOUNT_TYPE_LABELS = {
  percent: '% Off',
  fixed: '$ Off',
  free_shipping: 'Free Shipping',
};
const DEAL_TYPE_LABELS = {
  percent: '% Off Vendor Cart',
  fixed: '$ Off Vendor Cart',
  bogo: 'Buy One Get One',
  bundle: 'Bundle (Buy N+)',
  free_shipping: 'Free Shipping',
};

function isExpired(iso) {
  if (!iso) return false;
  try { return new Date(iso) < new Date(); } catch { return false; }
}

function formatExpiry(iso) {
  if (!iso) return 'Never expires';
  return `Expires ${new Date(iso).toLocaleDateString()}`;
}

// ── Coupon Modal ───────────────────────────────────────────────────────
function CouponModal({ initial, onClose, onSave }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial || {
    code: '', title: '', description: '',
    discount_type: 'percent', discount_value: 10,
    applies_to: 'vendor_cart', product_ids: [],
    total_use_cap: '', expires_at: '', active: true,
  });
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr('');
    const payload = { ...form };
    if (!payload.code?.trim()) { setErr('Code is required'); return; }
    payload.code = payload.code.trim().toUpperCase();
    payload.discount_value = Number(payload.discount_value) || 0;
    payload.total_use_cap = payload.total_use_cap ? Number(payload.total_use_cap) : null;
    payload.expires_at = payload.expires_at || null;
    try {
      await onSave(payload);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    }
  };

  return (
    <div className="cd-modal-bg" onClick={onClose}>
      <div className="cd-modal" onClick={e => e.stopPropagation()} data-testid="coupon-modal">
        <div className="cd-modal-head">
          <h3 className="cd-modal-title">{isEdit ? 'Edit Coupon' : 'New Coupon Code'}</h3>
          <button className="cd-icon-btn" onClick={onClose} data-testid="coupon-modal-close"><X size={14}/></button>
        </div>
        {err && <div className="cd-error"><AlertCircle size={16} /> {err}</div>}
        <div className="cd-grid-2">
          <div className="cd-field">
            <label>Code *</label>
            <Input
              value={form.code}
              onChange={e => set('code', e.target.value.toUpperCase())}
              placeholder="SPRING20"
              data-testid="coupon-code-input"
            />
          </div>
          <div className="cd-field">
            <label>Discount Type</label>
            <Select value={form.discount_type} onValueChange={v => set('discount_type', v)}>
              <SelectTrigger data-testid="coupon-discount-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                {Object.entries(DISCOUNT_TYPE_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {form.discount_type !== 'free_shipping' && (
          <div className="cd-field">
            <label>{form.discount_type === 'percent' ? 'Percent Off (0–100)' : 'Dollars Off'}</label>
            <Input
              type="number" min="0" max={form.discount_type === 'percent' ? 100 : undefined}
              value={form.discount_value}
              onChange={e => set('discount_value', e.target.value)}
              data-testid="coupon-discount-value"
            />
          </div>
        )}
        <div className="cd-field">
          <label>Title (optional)</label>
          <Input value={form.title || ''} onChange={e => set('title', e.target.value)} placeholder="Spring Sale 20% off" />
        </div>
        <div className="cd-grid-2">
          <div className="cd-field">
            <label>Total Use Cap (optional)</label>
            <Input
              type="number" min="1"
              value={form.total_use_cap || ''}
              onChange={e => set('total_use_cap', e.target.value)}
              placeholder="e.g. 100"
              data-testid="coupon-use-cap"
            />
          </div>
          <div className="cd-field">
            <label>Expires At (optional)</label>
            <Input
              type="datetime-local"
              value={form.expires_at ? form.expires_at.substring(0, 16) : ''}
              onChange={e => set('expires_at', e.target.value ? new Date(e.target.value).toISOString() : '')}
              data-testid="coupon-expires-at"
            />
          </div>
        </div>
        <div className="cd-field" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <Switch checked={form.active} onCheckedChange={v => set('active', v)} data-testid="coupon-active-toggle" />
          <span style={{ fontSize: 13 }}>{form.active ? 'Active' : 'Inactive (hidden from customers)'}</span>
        </div>
        <div className="cd-modal-foot">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="coupon-save-btn">{isEdit ? 'Save Changes' : 'Create Coupon'}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Deal Modal ─────────────────────────────────────────────────────────
function DealModal({ initial, onClose, onSave }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial || {
    title: '', description: '',
    deal_type: 'percent', discount_value: 10,
    bogo_buy_qty: 1, bogo_get_qty: 1, bogo_discount_percent: 100,
    bundle_min_qty: 3,
    applies_to: 'vendor_cart', product_ids: [],
    expires_at: '', active: true,
  });
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr('');
    if (!form.title?.trim()) { setErr('Title is required'); return; }
    const payload = {
      ...form,
      discount_value: Number(form.discount_value) || 0,
      bogo_buy_qty: Number(form.bogo_buy_qty) || 1,
      bogo_get_qty: Number(form.bogo_get_qty) || 1,
      bogo_discount_percent: Number(form.bogo_discount_percent) || 100,
      bundle_min_qty: Number(form.bundle_min_qty) || 2,
      expires_at: form.expires_at || null,
    };
    try {
      await onSave(payload);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    }
  };

  return (
    <div className="cd-modal-bg" onClick={onClose}>
      <div className="cd-modal" onClick={e => e.stopPropagation()} data-testid="deal-modal">
        <div className="cd-modal-head">
          <h3 className="cd-modal-title">{isEdit ? 'Edit Deal' : 'New Automatic Deal'}</h3>
          <button className="cd-icon-btn" onClick={onClose}><X size={14}/></button>
        </div>
        {err && <div className="cd-error"><AlertCircle size={16}/> {err}</div>}
        <div className="cd-field">
          <label>Title *</label>
          <Input value={form.title} onChange={e => set('title', e.target.value)}
            placeholder="Bundle 3+ for 15% off" data-testid="deal-title-input" />
        </div>
        <div className="cd-field">
          <label>Deal Type</label>
          <Select value={form.deal_type} onValueChange={v => set('deal_type', v)}>
            <SelectTrigger data-testid="deal-type-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {Object.entries(DEAL_TYPE_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(form.deal_type === 'percent' || form.deal_type === 'fixed') && (
          <div className="cd-field">
            <label>{form.deal_type === 'percent' ? 'Percent Off' : 'Dollars Off'}</label>
            <Input type="number" value={form.discount_value}
              onChange={e => set('discount_value', e.target.value)} data-testid="deal-discount-value" />
          </div>
        )}
        {form.deal_type === 'bogo' && (
          <>
            <div className="cd-grid-2">
              <div className="cd-field">
                <label>Buy Qty</label>
                <Input type="number" min="1" value={form.bogo_buy_qty}
                  onChange={e => set('bogo_buy_qty', e.target.value)} data-testid="deal-bogo-buy" />
              </div>
              <div className="cd-field">
                <label>Get Qty</label>
                <Input type="number" min="1" value={form.bogo_get_qty}
                  onChange={e => set('bogo_get_qty', e.target.value)} data-testid="deal-bogo-get" />
              </div>
            </div>
            <div className="cd-field">
              <label>% Off the "Get" Items (100 = free, 50 = half off)</label>
              <Input type="number" min="0" max="100" value={form.bogo_discount_percent}
                onChange={e => set('bogo_discount_percent', e.target.value)} data-testid="deal-bogo-pct" />
            </div>
          </>
        )}
        {form.deal_type === 'bundle' && (
          <div className="cd-grid-2">
            <div className="cd-field">
              <label>Minimum Qty</label>
              <Input type="number" min="2" value={form.bundle_min_qty}
                onChange={e => set('bundle_min_qty', e.target.value)} data-testid="deal-bundle-min" />
            </div>
            <div className="cd-field">
              <label>Percent Off</label>
              <Input type="number" min="0" max="100" value={form.discount_value}
                onChange={e => set('discount_value', e.target.value)} data-testid="deal-bundle-pct" />
            </div>
          </div>
        )}
        <div className="cd-field">
          <label>Expires At (optional)</label>
          <Input type="datetime-local"
            value={form.expires_at ? form.expires_at.substring(0, 16) : ''}
            onChange={e => set('expires_at', e.target.value ? new Date(e.target.value).toISOString() : '')}
            data-testid="deal-expires-at" />
        </div>
        <div className="cd-field" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <Switch checked={form.active} onCheckedChange={v => set('active', v)} data-testid="deal-active-toggle" />
          <span style={{ fontSize: 13 }}>{form.active ? 'Active' : 'Paused'}</span>
        </div>
        <div className="cd-modal-foot">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="deal-save-btn">{isEdit ? 'Save Changes' : 'Create Deal'}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Tab ───────────────────────────────────────────────────────────
export default function CouponsDealsTab() {
  const [coupons, setCoupons] = useState([]);
  const [deals, setDeals] = useState([]);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([
        axios.get(`${API}/vendor/coupons`, { headers: headers() }),
        axios.get(`${API}/vendor/deals`, { headers: headers() }),
      ]);
      setCoupons(c.data || []);
      setDeals(d.data || []);
    } catch (e) {
      console.error('Load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveCoupon = async (payload) => {
    if (editingCoupon) {
      await axios.patch(`${API}/vendor/coupons/${editingCoupon.id}`, payload, { headers: headers() });
    } else {
      await axios.post(`${API}/vendor/coupons`, payload, { headers: headers() });
    }
    await load();
  };
  const saveDeal = async (payload) => {
    if (editingDeal) {
      await axios.patch(`${API}/vendor/deals/${editingDeal.id}`, payload, { headers: headers() });
    } else {
      await axios.post(`${API}/vendor/deals`, payload, { headers: headers() });
    }
    await load();
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon? Customers will no longer be able to use it.')) return;
    await axios.delete(`${API}/vendor/coupons/${id}`, { headers: headers() });
    await load();
  };
  const deleteDeal = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    await axios.delete(`${API}/vendor/deals/${id}`, { headers: headers() });
    await load();
  };

  return (
    <>
      <style>{styles}</style>
      <div className="cd-wrap" data-testid="coupons-deals-tab">
        <p style={{ color: 'rgba(245,240,232,0.6)', fontSize: 13, marginBottom: 20 }}>
          Coupons are manual codes customers enter on your storefront. Deals are automatic and apply when criteria are met.
          Best discount wins (no stacking). You absorb the discount cost — the platform fee is calculated after the discount.
        </p>

        {/* ── Coupons ─────────────────────────────────────────── */}
        <div className="cd-section">
          <div className="cd-section-head">
            <div>
              <h3 className="cd-section-title"><Tag size={20} color="#c9a84c"/> Coupon Codes</h3>
              <p className="cd-section-sub">Customers enter these codes on your storefront page</p>
            </div>
            <Button
              size="sm"
              onClick={() => { setEditingCoupon(null); setShowCouponModal(true); }}
              data-testid="add-coupon-btn"
            >
              <Plus size={14}/> New Coupon
            </Button>
          </div>
          {loading ? <div className="cd-empty">Loading…</div> :
            coupons.length === 0 ? (
              <div className="cd-empty" data-testid="coupons-empty">No coupon codes yet. Create one to share with customers.</div>
            ) : (
              <div className="cd-list">
                {coupons.map(c => {
                  const expired = isExpired(c.expires_at);
                  return (
                    <div key={c.id} className="cd-row" data-testid={`coupon-row-${c.code}`}>
                      <div style={{ flex: 1 }}>
                        <div className="cd-row-title">
                          <span className="cd-code-chip">{c.code}</span>
                          {c.title && <span>{c.title}</span>}
                          {!c.active ? <span className="cd-badge inactive">Inactive</span>
                            : expired ? <span className="cd-badge expired">Expired</span>
                            : <span className="cd-badge active">Active</span>}
                        </div>
                        <div className="cd-row-meta">
                          <span>{DISCOUNT_TYPE_LABELS[c.discount_type]}{c.discount_type !== 'free_shipping' && `: ${c.discount_value}${c.discount_type === 'percent' ? '%' : '$'}`}</span>
                          <span>Used: {c.uses_count || 0}{c.total_use_cap ? `/${c.total_use_cap}` : ''}</span>
                          <span>{formatExpiry(c.expires_at)}</span>
                        </div>
                      </div>
                      <div className="cd-actions">
                        <button className="cd-icon-btn" onClick={() => { setEditingCoupon(c); setShowCouponModal(true); }} data-testid={`coupon-edit-${c.code}`}><Edit3 size={12}/></button>
                        <button className="cd-icon-btn danger" onClick={() => deleteCoupon(c.id)} data-testid={`coupon-delete-${c.code}`}><Trash2 size={12}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        {/* ── Deals ───────────────────────────────────────────── */}
        <div className="cd-section">
          <div className="cd-section-head">
            <div>
              <h3 className="cd-section-title"><Sparkles size={20} color="#c9a84c"/> Automatic Deals</h3>
              <p className="cd-section-sub">No code needed — applied automatically when customers qualify</p>
            </div>
            <Button
              size="sm"
              onClick={() => { setEditingDeal(null); setShowDealModal(true); }}
              data-testid="add-deal-btn"
            >
              <Plus size={14}/> New Deal
            </Button>
          </div>
          {loading ? <div className="cd-empty">Loading…</div> :
            deals.length === 0 ? (
              <div className="cd-empty" data-testid="deals-empty">No automatic deals yet. Try a BOGO or bundle discount.</div>
            ) : (
              <div className="cd-list">
                {deals.map(d => {
                  const expired = isExpired(d.expires_at);
                  return (
                    <div key={d.id} className="cd-row" data-testid={`deal-row-${d.id}`}>
                      <div style={{ flex: 1 }}>
                        <div className="cd-row-title">
                          <span>{d.title}</span>
                          {!d.active ? <span className="cd-badge inactive">Paused</span>
                            : expired ? <span className="cd-badge expired">Expired</span>
                            : <span className="cd-badge active">Active</span>}
                        </div>
                        <div className="cd-row-meta">
                          <span>{DEAL_TYPE_LABELS[d.deal_type]}</span>
                          {d.deal_type === 'percent' && <span>{d.discount_value}% off</span>}
                          {d.deal_type === 'fixed' && <span>${d.discount_value} off</span>}
                          {d.deal_type === 'bogo' && <span>Buy {d.bogo_buy_qty}, get {d.bogo_get_qty} @ {d.bogo_discount_percent}% off</span>}
                          {d.deal_type === 'bundle' && <span>Buy {d.bundle_min_qty}+ → {d.discount_value}% off</span>}
                          <span>{formatExpiry(d.expires_at)}</span>
                        </div>
                      </div>
                      <div className="cd-actions">
                        <button className="cd-icon-btn" onClick={() => { setEditingDeal(d); setShowDealModal(true); }} data-testid={`deal-edit-${d.id}`}><Edit3 size={12}/></button>
                        <button className="cd-icon-btn danger" onClick={() => deleteDeal(d.id)} data-testid={`deal-delete-${d.id}`}><Trash2 size={12}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        {showCouponModal && (
          <CouponModal
            initial={editingCoupon}
            onClose={() => setShowCouponModal(false)}
            onSave={saveCoupon}
          />
        )}
        {showDealModal && (
          <DealModal
            initial={editingDeal}
            onClose={() => setShowDealModal(false)}
            onSave={saveDeal}
          />
        )}
      </div>
    </>
  );
}
